import { useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { deserializeSearchParams, serializeSearchParams, SearchParams } from '../utils/urlParams';
import { SearchState } from '../types/search';
import { TaggedJourney } from '../utils/mapJourneysData';

interface UseUrlSyncProps {
  searchState: SearchState;
  journeys: TaggedJourney[];
  onStateUpdate: (params: SearchParams) => void;
  onFetch: () => void;
}

/**
 * Hook to synchronize URL search parameters with component state
 * Handles reading from URL, updating state, and syncing state changes back to URL
 */
export const useUrlSync = ({
  searchState,
  journeys,
  onStateUpdate,
  onFetch,
}: UseUrlSyncProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isUpdatingFromUrlRef = useRef(false);
  const hasInitializedFromUrlRef = useRef(false);
  const pendingUrlParamsRef = useRef<SearchParams | null>(null);

  // Keep stable refs to callbacks so effects don't need them as dependencies.
  // This prevents inline arrow functions in the parent from causing effect re-runs.
  const onStateUpdateRef = useRef(onStateUpdate);
  const onFetchRef = useRef(onFetch);
  useEffect(() => {
    onStateUpdateRef.current = onStateUpdate;
    onFetchRef.current = onFetch;
  });

  const {
    originId,
    destinationId,
    originName,
    destinationName,
    date,
    tripType,
    options,
  } = searchState;

  // Read URL parameters on mount and when they change externally (e.g., browser back/forward)
  useEffect(() => {
    const urlParams = deserializeSearchParams(searchParams);

    // Check if URL has valid search parameters
    const hasUrlParams = urlParams.originId && urlParams.destinationId;

    if (hasUrlParams) {
      // Check if URL params differ from current state
      const urlOptionsStr = JSON.stringify(urlParams.options || {});
      const currentOptionsStr = JSON.stringify(options || {});

      const paramsChanged =
        urlParams.originId !== originId ||
        urlParams.destinationId !== destinationId ||
        urlParams.originName !== originName ||
        urlParams.destinationName !== destinationName ||
        urlParams.date !== date ||
        urlParams.tripType !== tripType ||
        urlOptionsStr !== currentOptionsStr;

      const isInitialLoad = !hasInitializedFromUrlRef.current;

      // Check if we already have journeys that match these params
      // If so, we're likely navigating back from a detail page, so don't trigger a new search
      const hasMatchingJourneys = journeys.length > 0 &&
        urlParams.originId === originId &&
        urlParams.destinationId === destinationId &&
        urlParams.date === date &&
        urlParams.tripType === tripType;

      // Only update state and trigger fetch if params changed AND we don't have matching journeys
      if ((paramsChanged || (isInitialLoad && hasUrlParams)) && !hasMatchingJourneys) {
        isUpdatingFromUrlRef.current = true;
        pendingUrlParamsRef.current = urlParams;
        onStateUpdateRef.current(urlParams);
      } else if (!hasInitializedFromUrlRef.current) {
        hasInitializedFromUrlRef.current = true;
      }
    } else if (!hasInitializedFromUrlRef.current) {
      hasInitializedFromUrlRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Trigger fetch after state has been updated from URL params.
  // NOTE: onFetch is intentionally accessed via ref and excluded from deps to prevent
  // inline parent callbacks (recreated every render) from causing an infinite loop.
  useEffect(() => {
    if (pendingUrlParamsRef.current &&
        originId === pendingUrlParamsRef.current.originId &&
        destinationId === pendingUrlParamsRef.current.destinationId) {

      const pending = pendingUrlParamsRef.current;
      pendingUrlParamsRef.current = null;

      // Check if we're navigating back from a journey detail page
      const isNavigatingBackFromDetail = location.state?.from?.startsWith('/journey/');
      const hasMatchingJourneys = journeys.length > 0 &&
        originId === pending.originId &&
        destinationId === pending.destinationId;

      const previousPath = document.referrer ? new URL(document.referrer).pathname : '';
      const wasOnJourneyDetail = previousPath.startsWith('/journey/');

      if ((isNavigatingBackFromDetail || wasOnJourneyDetail) && hasMatchingJourneys) {
        // Just navigating back - don't fetch, use journeys from memory
        hasInitializedFromUrlRef.current = true;
        isUpdatingFromUrlRef.current = false;
      } else {
        // New search or no matching journeys - fetch
        hasInitializedFromUrlRef.current = true;
        onFetchRef.current();
        isUpdatingFromUrlRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originId, destinationId, date, tripType, location.state]);

  // Update URL when search parameters change (but skip if we're updating from URL)
  useEffect(() => {
    if (isUpdatingFromUrlRef.current) {
      return;
    }

    if (originId && destinationId && hasInitializedFromUrlRef.current) {
      const searchDate = date || new Date().toISOString();
      const newParams = serializeSearchParams(
        originId,
        destinationId,
        originName,
        destinationName,
        searchDate,
        tripType,
        options
      );

      const currentParamsString = searchParams.toString();
      const newParamsString = newParams.toString();

      if (currentParamsString !== newParamsString) {
        setSearchParams(newParams, { replace: true });
      }
    } else if (hasInitializedFromUrlRef.current && searchParams.toString()) {
      if (!originId || !destinationId) {
        setSearchParams(new URLSearchParams(), { replace: true });
      }
    }
  }, [originId, destinationId, originName, destinationName, date, tripType, options, searchParams, setSearchParams]);
};
